import React from 'react';
import {
    MDBBtn,
    MDBInput,
} from 'mdb-react-ui-kit';
import axios from 'axios';
import { baseURL } from '../api/baseURL';
import Loader from '../Loader';
import Modal from '../Modal';
import { connect } from 'react-redux';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import AdminShell from './layout/AdminShell';
import { countPillStyle } from './layout/listUi';

const TAG_CHIP_STYLE = (borderColor: string): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 6,
    border: `1px solid ${borderColor}`, borderRadius: 20,
    padding: '5px 6px 5px 12px', fontSize: 12, fontWeight: 700,
    background: 'rgba(255,255,255,0.03)',
});
const TAG_REMOVE_STYLE: React.CSSProperties = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 18, height: 18, borderRadius: '50%', cursor: 'pointer',
    background: 'rgba(255,255,255,0.08)', fontSize: 12, lineHeight: 1,
};

function formatHourLabel(hour: number): string {
    const h12 = hour % 12 === 0 ? 12 : hour % 12;
    const suffix = hour < 12 ? 'AM' : 'PM';
    return `${h12} ${suffix}`;
}

/** Client-reported: raw "14, 15, 16, 17h" chips were hard to read — the new
 * time-range picker always produces a contiguous block, so show it as a
 * clean "2 PM – 6 PM" range. Falls back to a comma list for any older
 * non-contiguous entry (e.g. from before this picker existed). */
function formatHoursLabel(hours: number[]): string {
    const sorted = [...hours].sort((a, b) => a - b);
    const isContiguous = sorted.every((h, i) => i === 0 || h === sorted[i - 1] + 1);
    if (isContiguous && sorted.length > 0) {
        const endExclusive = sorted[sorted.length - 1] + 1;
        return `${formatHourLabel(sorted[0])} – ${formatHourLabel(endExclusive === 24 ? 0 : endExclusive)}`;
    }
    return sorted.map((h) => formatHourLabel(h)).join(', ');
}


type State = {
    requested: boolean,
    err: boolean,
    unavailableDates: any,
    // Hour-level blocking + service area allow/block list (Phase 2 §8) —
    // fetched separately from a dedicated endpoint rather than threaded as
    // props through every page (unlike unavailableDates, which was already
    // wired that way before this change), so no call-site changes needed.
    unavailableSlots: { date: string, hours: number[] }[],
    serviceAreasAllowed: string[],
    serviceAreasBlocked: string[],
    newSlotDateObj: Date | null,
    newSlotFromTime: string,
    newSlotToTime: string,
    newAllowedArea: string,
    newBlockedArea: string,
};

class AddUnavailableDates extends React.Component<{ token?: string }> {

    state: State = {
        requested: false,
        err: false,
        unavailableDates: [],
        unavailableSlots: [],
        serviceAreasAllowed: [],
        serviceAreasBlocked: [],
        newSlotDateObj: null,
        newSlotFromTime: '',
        newSlotToTime: '',
        newAllowedArea: '',
        newBlockedArea: '',
    }

    componentDidMount(): void {
        if (this.props.token) {
            axios.get(`${baseURL}/unavailable-config`, { headers: { Authorization: `Bearer ${this.props.token}` } })
                .then((response: any) => {
                    if (response?.data?.message === "success") {
                        this.setState({
                            unavailableDates: response.data.unavailableDates || [],
                            unavailableSlots: response.data.unavailableSlots || [],
                            serviceAreasAllowed: response.data.serviceAreas?.allowed || [],
                            serviceAreasBlocked: response.data.serviceAreas?.blocked || [],
                        });
                    }
                })
                .catch(() => {});
        }
    }

    updateUnavailableDates = () => {
        if (!this.state.requested) {
            this.setState({ err: false, requested: true });
            axios.post(`${baseURL}/update-unavailable-dates`,
                {
                    unavailableDates: this.state.unavailableDates,
                    unavailableSlots: this.state.unavailableSlots,
                    serviceAreas: { allowed: this.state.serviceAreasAllowed, blocked: this.state.serviceAreasBlocked },
                },
                {
                    headers: { Authorization: `Bearer ${this.props.token}` }
                })
                .then((response: any) => {
                  //console.log(response);
                    if (response?.data?.message === "success") {
                        window.location.reload();
                    }
                    else {
                        this.setState({ err: true, requested: false });
                    }
                })
                .catch((error: any) => {
                  //console.log(error);
                    this.setState({ err: true, requested: false });
                })
        }
    }

    addSlot = () => {
        const { newSlotDateObj, newSlotFromTime, newSlotToTime } = this.state;
        if (!newSlotDateObj || !newSlotFromTime || !newSlotToTime) return;

        const [fromH] = newSlotFromTime.split(':').map(Number);
        const [toH, toM] = newSlotToTime.split(':').map(Number);
        if (!Number.isFinite(fromH) || !Number.isFinite(toH)) return;

        // A "to" time with a non-zero minute rounds up to cover that partial
        // hour (e.g. 6:30 PM end blocks the 18:00 hour too); an end of 00:00
        // means "through midnight" (hour 24, exclusive). Same-day range only —
        // an end at or before the start time is invalid and ignored.
        let toHourExclusive = toM > 0 ? toH + 1 : toH;
        if (toHourExclusive === 0) toHourExclusive = 24;
        if (toHourExclusive <= fromH) return;
        const hours: number[] = [];
        for (let h = fromH; h < toHourExclusive; h++) hours.push(h);

        const year = newSlotDateObj.getFullYear();
        const month = String(newSlotDateObj.getMonth() + 1).padStart(2, '0');
        const day = String(newSlotDateObj.getDate()).padStart(2, '0');

        this.setState({
            unavailableSlots: [...this.state.unavailableSlots, { date: `${year}-${month}-${day}`, hours }],
            newSlotDateObj: null, newSlotFromTime: '', newSlotToTime: '',
        });
    }

    removeSlot = (idx: number) => {
        this.setState({ unavailableSlots: this.state.unavailableSlots.filter((_: any, i: number) => i !== idx) });
    }

    addArea = (kind: 'allowed' | 'blocked') => {
        const key = kind === 'allowed' ? 'newAllowedArea' : 'newBlockedArea';
        const listKey = kind === 'allowed' ? 'serviceAreasAllowed' : 'serviceAreasBlocked';
        const value = (this.state as any)[key].trim();
        if (!value) return;
        this.setState({ [listKey]: [...(this.state as any)[listKey], value], [key]: '' } as any);
    }

    removeArea = (kind: 'allowed' | 'blocked', area: string) => {
        const listKey = kind === 'allowed' ? 'serviceAreasAllowed' : 'serviceAreasBlocked';
        this.setState({ [listKey]: (this.state as any)[listKey].filter((a: string) => a !== area) } as any);
    }

    toggleModal = () => {
        if (this.state.err) {
            this.setState({ err: false });
        }
    }

    removeDate = (dateString: string) => {
        if (dateString) {
            let temp = this.state.unavailableDates.filter((item: string) => (dateString !== item));
            this.setState({ unavailableDates: temp });
        }
    }

    render() {
      //console.log(this.state)
        return (
            <AdminShell activePage="service-blocking" title="Service Blocking" subtitle="Block specific dates, hours, and service areas">
                <Modal open={this.state.err} hideSec primaryText="Okay" title='Update' onConfirm={this.toggleModal} toggleOpen={this.toggleModal} content={<h6>Something went wrong, please try again later</h6>} />
                {this.state.requested ? <Loader /> : null}

                <div className="card" style={{ marginBottom: 16 }}>
                    <div className="card-body">
                        <h4 className="card-title">
                            Blocked Dates
                            {this.state.unavailableDates?.length ? <span style={countPillStyle}>{this.state.unavailableDates.length}</span> : null}
                        </h4>
                        <p style={{ fontSize: 12, opacity: 0.6, marginBottom: 14 }}>Whole days customers can't book any service on. Enforced live at checkout.</p>
                        {this.state.unavailableDates?.length ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 220, overflowY: 'auto', marginBottom: 14 }}>
                                {this.state.unavailableDates.map((dateString: string) => (
                                    <span key={dateString} style={TAG_CHIP_STYLE('rgba(255,255,255,0.18)')}>
                                        {dateString}
                                        <span style={TAG_REMOVE_STYLE} onClick={() => this.removeDate(dateString)}>×</span>
                                    </span>
                                ))}
                            </div>
                        ) : null}
                        <DatePicker
                            value={this.state.unavailableDates?.join(", ")}
                            onChange={(date: any) => {
                                let year = date.getFullYear();
                                let month = String(date.getMonth() + 1).padStart(2, '0');
                                let day = String(date.getDate()).padStart(2, '0');

                                this.setState({ unavailableDates: [...this.state.unavailableDates, `${year}-${month}-${day}`] })
                            }}
                            placeholderText="Select a date"
                            calendarClassName="custom-calendar"
                            popperPlacement="bottom-start"
                            customInput={<MDBBtn size="sm" color='primary' rippleColor='light'>+ Add Date</MDBBtn>}
                            minDate={new Date()}
                        />
                    </div>
                </div>

                <div className="card" style={{ marginBottom: 16 }}>
                    <div className="card-body">
                        <h4 className="card-title">
                            Blocked Hours
                            {this.state.unavailableSlots.length ? <span style={countPillStyle}>{this.state.unavailableSlots.length}</span> : null}
                        </h4>
                        <p style={{ fontSize: 12, opacity: 0.6, marginBottom: 14 }}>Specific hours on a specific date. Enforced live — a booking spanning any blocked hour is rejected at checkout.</p>
                        {this.state.unavailableSlots.length ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                                {this.state.unavailableSlots.map((slot, idx) => (
                                    <span key={idx} style={TAG_CHIP_STYLE('rgba(255,255,255,0.18)')}>
                                        {slot.date}: {formatHoursLabel(slot.hours)}
                                        <span style={TAG_REMOVE_STYLE} onClick={() => this.removeSlot(idx)}>×</span>
                                    </span>
                                ))}
                            </div>
                        ) : null}
                        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            <DatePicker
                                selected={this.state.newSlotDateObj}
                                onChange={(date: any) => this.setState({ newSlotDateObj: date })}
                                placeholderText="Select a date"
                                calendarClassName="custom-calendar"
                                popperPlacement="bottom-start"
                                dateFormat="d MMM yyyy"
                                customInput={<input className="form-control" style={{ minWidth: 140 }} />}
                                minDate={new Date()}
                            />
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <label style={{ fontSize: 10.5, opacity: 0.6 }}>From</label>
                                <input
                                    type="time"
                                    className="form-control"
                                    // Client-reported: the native time picker's own clock
                                    // icon/UI renders in a dark colour by default and was
                                    // invisible against this dark theme — color-scheme
                                    // hints the browser to render it (and the icon) light.
                                    style={{ minWidth: 110, colorScheme: 'dark' }}
                                    value={this.state.newSlotFromTime}
                                    onChange={(e) => this.setState({ newSlotFromTime: e.target.value })}
                                />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <label style={{ fontSize: 10.5, opacity: 0.6 }}>To</label>
                                <input
                                    type="time"
                                    className="form-control"
                                    style={{ minWidth: 110, colorScheme: 'dark' }}
                                    value={this.state.newSlotToTime}
                                    onChange={(e) => this.setState({ newSlotToTime: e.target.value })}
                                />
                            </div>
                            <MDBBtn size='sm' onClick={this.addSlot} disabled={!this.state.newSlotDateObj || !this.state.newSlotFromTime || !this.state.newSlotToTime}>+ Add</MDBBtn>
                        </div>
                    </div>
                </div>

                <div className="card" style={{ marginBottom: 16 }}>
                    <div className="card-body">
                        <h4 className="card-title">Service Areas</h4>
                        <p style={{ fontSize: 12, opacity: 0.6, marginBottom: 14 }}>
                            Blocked areas are rejected at checkout by matching the booking's geocoded locality. Allowed areas, when set, make the allowlist exclusive — only bookings inside a listed area are accepted.
                        </p>
                        <div style={{ marginBottom: 18 }}>
                            <label style={{ fontSize: 12, fontWeight: 700, opacity: 0.7, display: 'flex', alignItems: 'center', gap: 8 }}>
                                Allowed areas
                                {this.state.serviceAreasAllowed.length ? <span style={countPillStyle}>{this.state.serviceAreasAllowed.length}</span> : null}
                            </label>
                            {this.state.serviceAreasAllowed.length ? (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '8px 0' }}>
                                    {this.state.serviceAreasAllowed.map((area) => (
                                        <span key={area} style={TAG_CHIP_STYLE('#00d25b')}>
                                            {area}
                                            <span style={TAG_REMOVE_STYLE} onClick={() => this.removeArea('allowed', area)}>×</span>
                                        </span>
                                    ))}
                                </div>
                            ) : null}
                            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                <MDBInput contrast label="e.g. Vashi" type="text" value={this.state.newAllowedArea} onChange={(e) => this.setState({ newAllowedArea: e.target.value })} />
                                <MDBBtn size='sm' color='success' onClick={() => this.addArea('allowed')}>+ Add</MDBBtn>
                            </div>
                        </div>
                        <div>
                            <label style={{ fontSize: 12, fontWeight: 700, opacity: 0.7, display: 'flex', alignItems: 'center', gap: 8 }}>
                                Blocked areas
                                {this.state.serviceAreasBlocked.length ? <span style={countPillStyle}>{this.state.serviceAreasBlocked.length}</span> : null}
                            </label>
                            {this.state.serviceAreasBlocked.length ? (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '8px 0' }}>
                                    {this.state.serviceAreasBlocked.map((area) => (
                                        <span key={area} style={TAG_CHIP_STYLE('#fc424a')}>
                                            {area}
                                            <span style={TAG_REMOVE_STYLE} onClick={() => this.removeArea('blocked', area)}>×</span>
                                        </span>
                                    ))}
                                </div>
                            ) : null}
                            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                <MDBInput contrast label="e.g. Dharavi" type="text" value={this.state.newBlockedArea} onChange={(e) => this.setState({ newBlockedArea: e.target.value })} />
                                <MDBBtn size='sm' color='danger' onClick={() => this.addArea('blocked')}>+ Add</MDBBtn>
                            </div>
                        </div>
                    </div>
                </div>

                <MDBBtn onClick={() => { this.updateUnavailableDates() }}>Save changes</MDBBtn>
            </AdminShell>
        );
    }
}


const mapDispatchToProps = (dispatch: Function) => {
    return {
        dispatch
    }
}

const mapStateToProps = (state: any, ownProps: any) => {
    return {
        token: state.auth.token
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(AddUnavailableDates);